import type { ChargebackRow } from '@commissionsight/sdk';
import { formatPeriodLong } from '@commissionsight/shared';
import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../api.js';
import { PageTabs, chargebacksTabs } from '../components/PageTabs.js';
import { PeriodPicker } from '../components/PeriodPicker.js';
import { WorkspaceFilter } from '../components/WorkspaceFilter.js';
import { Card, Field, Loading, inputClass } from '../components/ui.js';
import { formatCurrency } from '../lib/status.js';
import { useCarriers } from '../lib/useCarriers.js';

export function Chargebacks() {
  const { client, baseUrl, token, entitlements } = useAuth();
  const [searchParams] = useSearchParams();
  const { carriers } = useCarriers();
  const [carrierId, setCarrierId] = useState(searchParams.get('carrierId') ?? '');
  const [workspaceId, setWorkspaceId] = useState(searchParams.get('workspaceId') ?? '');
  const [period, setPeriod] = useState('');
  const [rows, setRows] = useState<ChargebackRow[] | null>(null);
  const [resolvedPeriod, setResolvedPeriod] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Per-row dispute-creation state (keyed by policyRefId), so each chargeback line
  // shows its own busy / done / error feedback.
  const [disputeState, setDisputeState] = useState<Record<string, 'busy' | 'done' | 'error'>>({});
  // Policies that already have an OPEN chargeback dispute for the viewed period —
  // those rows show a solid red flag and can't spawn a duplicate.
  const [disputedPolicies, setDisputedPolicies] = useState<Set<string>>(new Set());

  // A row is "already disputed" if a persisted open dispute exists for it, or we
  // just created one this session.
  const alreadyDisputed = (policyRefId: string) =>
    disputedPolicies.has(policyRefId) || disputeState[policyRefId] === 'done';

  // One-click: turn a chargeback into a tracked dispute. The disputed figure is the
  // clawed-back amount as a NEGATIVE (money the carrier took back), linked to the
  // member + policy + period so it shows on their journey and lands in the audit log.
  const createDispute = async (r: ChargebackRow) => {
    setDisputeState((s) => ({ ...s, [r.policyRefId]: 'busy' }));
    try {
      const [yy, mm] = (resolvedPeriod ?? '').split('-');
      const res = await fetch(`${baseUrl.replace(/\/$/, '')}/disputes`, {
        method: 'POST',
        headers: { authorization: `Bearer ${token}`, 'content-type': 'application/json' },
        body: JSON.stringify({
          title: `Chargeback dispute — ${r.memberExternalId ?? r.memberRefId}${r.policyNumber ? ` (${r.policyNumber})` : ''}`,
          amountCents: -Math.round(r.chargebackAmount * 100),
          source: 'chargeback',
          memberRefId: r.memberRefId,
          policyRefId: r.policyRefId,
          carrierId: carrierId || undefined,
          periodYear: yy ? Number(yy) : undefined,
          periodMonth: mm ? Number(mm) : undefined,
          notes: r.originalPayout
            ? `From chargeback. Original payout ${formatCurrency(r.originalPayout.amount)} in ${r.originalPayout.period}.`
            : 'From chargeback. No original payout on record.',
        }),
      });
      if (!res.ok) throw new Error();
      setDisputeState((s) => ({ ...s, [r.policyRefId]: 'done' }));
    } catch {
      setDisputeState((s) => ({ ...s, [r.policyRefId]: 'error' }));
    }
  };

  useEffect(() => {
    let active = true;
    setRows(null);
    setError(null);
    client
      .listChargebacks({
        period: period || undefined,
        carrierId: carrierId || undefined,
        workspaceId: workspaceId || undefined,
      })
      .then((r) => {
        if (!active) return;
        setRows(r.data);
        setResolvedPeriod(r.period);
        if (!period && r.period) setPeriod(r.period);
      })
      .catch((e) => active && setError(e instanceof Error ? e.message : 'Failed to load'));
    return () => {
      active = false;
    };
  }, [client, period, carrierId, workspaceId]);

  // Load the account's OPEN chargeback disputes for the viewed period so already-
  // disputed rows are flagged (and can't be re-disputed). Re-runs when the period
  // resolves or changes.
  useEffect(() => {
    if (!entitlements.disputes || !resolvedPeriod) return;
    const [yy, mm] = resolvedPeriod.split('-');
    if (!yy || !mm) return;
    let active = true;
    fetch(
      `${baseUrl.replace(/\/$/, '')}/disputes?periodYear=${Number(yy)}&periodMonth=${Number(mm)}&limit=200`,
      { headers: { authorization: `Bearer ${token}` } },
    )
      .then((r) => (r.ok ? r.json() : { data: [] }))
      .then((b) => {
        if (!active) return;
        const open = new Set<string>(
          (b.data ?? [])
            .filter(
              (d: { source?: string; status?: string; policyRefId?: string | null }) =>
                d.source === 'chargeback' &&
                !!d.policyRefId &&
                d.status !== 'recovered' &&
                d.status !== 'written_off',
            )
            .map((d: { policyRefId: string }) => d.policyRefId),
        );
        setDisputedPolicies(open);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [baseUrl, token, entitlements.disputes, resolvedPeriod]);

  return (
    <div className="space-y-6">
      {chargebacksTabs(entitlements).length > 1 && (
        <PageTabs tabs={chargebacksTabs(entitlements)} />
      )}
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold">Chargebacks</h1>
          <p className="mt-1 text-sm text-neutral-500">
            Commission the carrier clawed back — with the original payout (when they first paid, how
            much, and from which file) and whether the chargeback fully reverses it.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <WorkspaceFilter value={workspaceId} onChange={setWorkspaceId} />
          <div className="w-48">
            <Field label="Carrier">
              <select
                className={inputClass}
                value={carrierId}
                onChange={(e) => setCarrierId(e.target.value)}
              >
                <option value="">All carriers</option>
                {carriers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>
          <div className="w-48">
            <Field label="Period">
              <PeriodPicker value={period} onChange={setPeriod} placeholder="Latest period" />
            </Field>
          </div>
        </div>
      </div>

      {error && <p className="rounded bg-rose-50 px-3 py-2 text-sm text-rose-700">{error}</p>}

      <Card className="p-0">
        <div className="grid grid-cols-[1.4fr_1fr_1.6fr_0.8fr_auto] gap-4 border-b border-neutral-200 px-5 py-2 text-xs font-medium text-neutral-500 dark:border-neutral-800">
          <span>Member / policy</span>
          <span className="text-right">Clawed back</span>
          <span>Original payout (record 0)</span>
          <span className="text-right">Reverses?</span>
          {entitlements.disputes && <span className="text-right">Dispute</span>}
        </div>
        {!rows ? (
          <Loading label="Loading chargebacks…" />
        ) : rows.length === 0 ? (
          <p className="py-12 text-center text-sm text-neutral-500">
            No chargebacks for {formatPeriodLong(resolvedPeriod)}.
          </p>
        ) : (
          <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
            {rows.map((r) => (
              <div
                key={r.policyRefId}
                className="grid grid-cols-[1.4fr_1fr_1.6fr_0.8fr_auto] items-center gap-4 px-5 py-3 text-sm"
              >
                <div className="min-w-0">
                  <Link
                    to={`/members/${r.memberRefId}`}
                    className="truncate font-medium text-emerald-700 hover:underline dark:text-emerald-400"
                    title="View this member's full history"
                  >
                    {r.memberExternalId ?? r.memberRefId}
                  </Link>
                  <Link
                    to={`/policies/${r.policyRefId}`}
                    className="block truncate text-xs text-neutral-400 hover:text-emerald-600 hover:underline"
                  >
                    {r.policyNumber ?? '—'}
                    {r.planName ? ` · ${r.planName}` : ''}
                  </Link>
                </div>
                <div className="text-right font-semibold text-amber-600 dark:text-amber-400">
                  −{formatCurrency(r.chargebackAmount)}
                </div>
                <div className="min-w-0 text-xs">
                  {r.originalPayout ? (
                    <>
                      <div className="text-neutral-700 dark:text-neutral-300">
                        {formatCurrency(r.originalPayout.amount)} ·{' '}
                        {formatPeriodLong(r.originalPayout.period)}
                      </div>
                      <div className="truncate text-neutral-400">
                        {r.originalPayout.fileName ?? r.originalPayout.fileId ?? '—'}
                      </div>
                    </>
                  ) : (
                    <span className="text-rose-600 dark:text-rose-400">
                      No payout on record — carrier never paid
                    </span>
                  )}
                </div>
                <div className="text-right">
                  {(() => {
                    // Over-reversal: the clawback is larger than the original payout
                    // (e.g. carrier reversed more than it ever paid). Distinct from a
                    // "partial" reversal, which is smaller than the original.
                    const over =
                      r.paidOut &&
                      r.originalPayout != null &&
                      r.chargebackAmount > r.originalPayout.amount + 0.005;
                    if (r.fullyReversed)
                      return (
                        <span className="rounded-full bg-rose-100 px-2 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-900/40 dark:text-rose-300">
                          full
                        </span>
                      );
                    if (over)
                      return (
                        <span
                          className="rounded-full bg-rose-600 px-2 py-0.5 text-xs font-semibold text-white"
                          title="The clawback is larger than the original payout"
                        >
                          over
                        </span>
                      );
                    if (r.paidOut)
                      return (
                        <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                          partial
                        </span>
                      );
                    return <span className="text-xs text-neutral-400">—</span>;
                  })()}
                </div>
                {entitlements.disputes && (
                  <div className="text-right">
                    {alreadyDisputed(r.policyRefId) ? (
                      // Open dispute already exists for this chargeback — solid red
                      // flag, links to it, no duplicate creation.
                      <Link
                        to="/disputes"
                        title="An open dispute already exists for this chargeback — view it"
                        aria-label="Open dispute exists — view in Disputes"
                        className="inline-flex items-center justify-center rounded-md p-1.5 text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-950/40"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                          <line x1="4" y1="22" x2="4" y2="15" />
                        </svg>
                      </Link>
                    ) : (
                      <button
                        type="button"
                        onClick={() => createDispute(r)}
                        disabled={disputeState[r.policyRefId] === 'busy'}
                        title="Create a dispute from this chargeback"
                        aria-label="Create a dispute from this chargeback"
                        className={`inline-flex items-center justify-center rounded-md p-1.5 transition disabled:opacity-50 ${
                          disputeState[r.policyRefId] === 'error'
                            ? 'text-rose-600'
                            : 'text-neutral-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-950/40'
                        }`}
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                          <line x1="4" y1="22" x2="4" y2="15" />
                        </svg>
                      </button>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}
